import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Line } from '../objects/Line';
import { Arc } from '../objects/Arc';
import { DrawPoint, DrawLine, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

export class ParallelLineConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 150;
    const startY = options.y ?? 450;
    const size = options.size ?? 400;

    const pA = { x: startX, y: startY };
    const pB = { x: startX + size, y: startY };
    const pP = { x: startX + 120, y: startY - 140 }; // External point P
    const r = 100;

    const pTrans1 = { x: startX + 60, y: startY };

    // Point on top line parallel to AB
    const pTopRight = { x: pP.x + size * 0.7, y: pP.y };
    const pTopLeft = { x: pP.x - size * 0.3, y: pP.y };

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointP = new Point(this.renderer, pP.x, pP.y, 'P');

    const baseLine = new Line(this.renderer, pA, pB, false);
    const transLine = new Line(this.renderer, pP, pTrans1, true);

    // arc1/arc2: copy the transversal angle. CW from -108° to 0° = 108° small arc ✓
    const arc1 = new Arc(this.renderer, pTrans1, r, -Math.PI * 0.6, 0, true, true);
    const arc2 = new Arc(this.renderer, pP, r, -Math.PI * 0.6, 0, true, true);

    // Intersecting arc to copy angle: CCW -72°→-144° = 72° small arc ✓
    const pArc1Base = SVGRenderer.polarToCartesian(pTrans1, r, 0);
    const arc3 = new Arc(this.renderer, pArc1Base, r * 0.7, -Math.PI * 0.4, -Math.PI * 0.8, true, false);

    const parallelLine = new Line(this.renderer, pTopLeft, pTopRight, false);

    [pointA, pointB, pointP, baseLine, transLine, arc1, arc2, arc3, parallelLine].forEach((o) =>
      o.setVisible(false)
    );

    return [
      {
        id: 'step1',
        title: '1. Base Line AB and External Point P',
        description: 'Given line AB and external point P.',
        narration: 'Step 1: Given baseline AB and external point P.',
        duration: 1200,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB), new DrawPoint(pointP), new DrawLine(baseLine, this.ruler)],
      },
      {
        id: 'step2',
        title: '2. Draw Transversal Line through P',
        description: 'Draw transversal line connecting point P to baseline at Q.',
        narration: 'Step 2: Drawing transversal line connecting P to baseline.',
        duration: 1200,
        animations: [new DrawLine(transLine, this.ruler)],
      },
      {
        id: 'step3',
        title: '3. Copy Angle at P',
        description: 'Draw arcs at intersection Q and at point P with equal radius.',
        narration: 'Step 3: Copying corresponding angle at point P with compass.',
        duration: 2000,
        animations: [
          new MoveCompass(this.compass, pTrans1, r),
          new DrawArc(arc1, this.compass),
          new MoveCompass(this.compass, pP, r),
          new DrawArc(arc2, this.compass),
        ],
      },
      {
        id: 'step4',
        title: '4. Intersect Arc to Fix Angle',
        description: 'Measure angle opening with compass and mark arc intersection.',
        narration: 'Step 4: Marking arc intersection to match corresponding angle.',
        duration: 1500,
        animations: [new DrawArc(arc3, this.compass)],
      },
      {
        id: 'step5',
        title: '5. Draw Parallel Line through P',
        description: 'Using ruler, draw line through P and the intersection mark parallel to AB.',
        narration: 'Construction complete! Top line through P is strictly parallel to baseline AB.',
        duration: 1500,
        animations: [new Fade(this.compass, false), new DrawLine(parallelLine, this.ruler), new Highlight(parallelLine)],
      },
    ];
  }
}
